import type { Header, RowData } from '@tanstack/react-table';

import { useTranslation } from 'react-i18next';

import { clsx } from '~/styles/utils';

import type { AppTableFeatures } from './Table.hooks';

import { Icon } from '../Icon/Icon';
import { useTableGridContext } from './TableGrid.context';
import {
  alignmentStyles,
  isResizingStyle,
  mobileOnlyStyle,
  sortableStyle,
  tableHeaderCellStyle,
  tableHeaderResizerLineStyle,
  tableHeaderResizerStyle,
  tableHeaderRowStyle,
  tableHeaderTextStyle,
} from './TableHeader.css';
import { useTableHeaderResize } from './TableHeader.hooks';
import { useTableContext } from './TableRoot.context';

export const TableHeader = () => {
  const { getHeaderGroups } = useTableContext();

  return (
    <div className={tableHeaderRowStyle} role="row">
      {getHeaderGroups()
        .flatMap((headerGroup) => headerGroup.headers)
        .map((header) => (
          <TableHeaderCell header={header} key={header.id} />
        ))}
    </div>
  );
};

const TableHeaderCell = ({ header }: { header: Header<AppTableFeatures, RowData> }) => {
  const table = useTableContext();
  const { resetGridTemplateColumns } = useTableGridContext();
  const { headerCellRef, isResizing, onMouseDown } = useTableHeaderResize(header);
  const { t } = useTranslation(['common']);

  const alignment = header.column.columnDef.meta?.styles?.justify ?? 'start';
  const mobileOnly = !!header.column.columnDef.meta?.mobile;

  const canSort = header.column.getCanSort();
  const sortDirection = header.column.getIsSorted();
  const headerLabel = typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : undefined;

  const isTextHeader = headerLabel !== undefined;

  return (
    <div
      aria-sort={sortDirection ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
      className={clsx(tableHeaderCellStyle, alignmentStyles[alignment], mobileOnly && mobileOnlyStyle)}
      key={header.id}
      ref={headerCellRef}
      role="columnheader"
    >
      {canSort ? (
        <button
          aria-label={headerLabel ? t('common:datatable.sortBy', { column: headerLabel }) : undefined}
          className={clsx(isTextHeader && tableHeaderTextStyle, sortableStyle)}
          onClick={header.column.getToggleSortingHandler()}
          type="button"
        >
          <table.FlexRender header={header} />
        </button>
      ) : (
        <span className={isTextHeader ? tableHeaderTextStyle : undefined}>
          <table.FlexRender header={header} />
        </span>
      )}

      {sortDirection && <Icon name={sortDirection === 'asc' ? 'ArrowUp' : 'ArrowDown'} size={20} />}

      {!!header.column.columnDef.meta?.isResizable && (
        <div
          aria-hidden
          className={clsx(tableHeaderResizerStyle, isResizing && isResizingStyle)}
          onDoubleClick={resetGridTemplateColumns}
          onMouseDown={onMouseDown}
        >
          <span className={tableHeaderResizerLineStyle} />
        </div>
      )}
    </div>
  );
};
